'use client'

import { useState, useTransition, useEffect, useId } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from 'sonner'
import { Loader2, Mail } from 'lucide-react'
import {
  signInWithEmail,
  signUpWithEmail,
  exchangeCodeForSession,
  type SignInUpResult,
} from './actions'

interface AuthFormProps {
  error?: string
  success?: string
  initialMode?: string
}

export function AuthForm({ error, success, initialMode }: AuthFormProps) {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isPending, startTransition] = useTransition()
  const [isSignUp, setIsSignUp] = useState(initialMode === 'signup')
  const [emailSent, setEmailSent] = useState(false)
  const [lastEmail, setLastEmail] = useState('')
  const [isProcessingCode, setIsProcessingCode] = useState(false)
  const emailId = useId()
  const firstNameId = useId()
  const lastNameId = useId()
  const companyNameId = useId()

  // Handle OAuth callback code
  useEffect(() => {
    const code = searchParams.get('code')
    if (code && !isProcessingCode) {
      setIsProcessingCode(true)
      startTransition(async () => {
        const result = await exchangeCodeForSession(code)

        if (result?.error) {
          toast.error('Authentication Error', {
            description: result.error,
          })
          router.replace('/auth')
        } else {
          toast.success('Success!', {
            description: 'You have been successfully signed in.',
          })
          router.push('/')
        }
        setIsProcessingCode(false)
      })
    }
  }, [searchParams, router, isProcessingCode])

  // Show success/error messages from URL params
  useEffect(() => {
    if (error) {
      toast.error('Authentication Error', {
        description: error,
      })
    }
    if (success) {
      toast.success('Success', {
        description: success,
      })
    }
  }, [error, success])

  const handleSubmit = async (formData: FormData) => {
    startTransition(async () => {
      const email = formData.get('email') as string
      setLastEmail(email)

      let result: SignInUpResult
      if (isSignUp) {
        result = await signUpWithEmail(formData)
      } else {
        result = await signInWithEmail(formData)
      }

      if (result?.error && !result?.error?.includes('NEXT_REDIRECT')) {
        toast.error('Authentication Error', {
          description: result.error,
        })
      } else if (result?.success) {
        setEmailSent(true)
        toast.success('Magic Link Sent!', {
          description: result.message,
        })
      }
    })
  }

  const resetForm = () => {
    setEmailSent(false)
    setLastEmail('')
  }

  if (emailSent) {
    return (
      <div className="w-full max-w-sm text-center space-y-6">
        <div className="mx-auto h-16 w-16 bg-primary/10 rounded-full flex items-center justify-center">
          <Mail className="h-8 w-8 text-primary" />
        </div>
        <div className="space-y-2">
          <h2 className="text-2xl font-bold">Check Your Email</h2>
          <p className="text-muted-foreground">
            We've sent a magic link to <strong>{lastEmail}</strong>
          </p>
        </div>
        <p className="text-sm text-muted-foreground">
          Click the link in your email to {isSignUp ? 'complete your account setup' : 'sign in'}.
        </p>
        <Button variant="outline" className="w-full" onClick={resetForm}>
          Send Another Link
        </Button>
      </div>
    )
  }

  return (
    <div className="w-full max-w-sm space-y-8">
      {/* Header */}
      <div className="space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">
          {isSignUp ? 'Create Account' : 'Welcome Back'}
        </h2>
        <p className="text-muted-foreground">
          {isSignUp ? 'Enter your details to create your account' : 'Enter your email to sign in'}
        </p>
      </div>

      {/* Form */}
      <form action={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor={emailId}>Email</Label>
          <Input
            id={emailId}
            name="email"
            type="email"
            placeholder="you@example.com"
            required
            disabled={isPending}
          />
        </div>

        {isSignUp && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={firstNameId}>First Name</Label>
                <Input
                  id={firstNameId}
                  name="firstName"
                  type="text"
                  placeholder="John"
                  required
                  disabled={isPending}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={lastNameId}>Last Name</Label>
                <Input
                  id={lastNameId}
                  name="lastName"
                  type="text"
                  placeholder="Doe"
                  required
                  disabled={isPending}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={companyNameId}>
                Company <span className="text-muted-foreground font-normal">(Optional)</span>
              </Label>
              <Input
                id={companyNameId}
                name="companyName"
                type="text"
                placeholder="Acme Inc."
                disabled={isPending}
              />
            </div>
          </>
        )}

        <Button type="submit" className="w-full" size="lg" disabled={isPending}>
          {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {isSignUp ? 'Create Account' : 'Send Magic Link'}
        </Button>
      </form>

      {/* Toggle sign in / sign up */}
      <p className="text-center text-sm text-muted-foreground">
        {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
        <button
          type="button"
          onClick={() => setIsSignUp(!isSignUp)}
          disabled={isPending}
          className="font-medium text-primary underline-offset-4 hover:underline disabled:opacity-50"
        >
          {isSignUp ? 'Sign in' : 'Sign up'}
        </button>
      </p>
    </div>
  )
}
